import { gql } from '@apollo/client';
import { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ssrRevalidateHomeData } from '../../../graphdebate/src/utils/ssrRevalidate';
import { confirmationToast, failureToast } from '../components/Toast';
import { useAppApolloClient } from '../hooks/useAppApolloClient';
import { useWebsite } from '../hooks/useWebsite';
import type { Author, Profile, Topic, User } from '../types/api';
import type { AppApolloClient } from '../types/common-models';

export const aboveMessagesAnchor = 'messageListTop';
export const ANSWER_ANCHOR = 'answer';

// `id` is likely useless, but let's keep it for now until we are able to test it well (legacy code).
export const TMP_UPDATE_STATUS_TOPIC_FRAGMENT = gql`
  fragment TMP_UPDATE_STATUS_TOPIC on Topic {
    id
    status
  }
`;

export function deleteThreadInCache(client: AppApolloClient, topicId: string | undefined) {
  if (!topicId) {
    console.warn('Cannot delete topic from cache with an undefined topic ID.');
    return;
  }
  client.writeFragment({
    fragment: TMP_UPDATE_STATUS_TOPIC_FRAGMENT,
    id: `Topic:${topicId}`,
    data: {
      id: topicId,
      status: 'deleted',
    },
  });
}

interface DeleteTopicsArgs {
  pageId: string | number;
  motifId?: string | number;
  motif?: string;
}
export type DeleteTopicsArgs2 = Omit<DeleteTopicsArgs, 'pageId'>;

const messages = defineMessages({
  confirmationLabel: { defaultMessage: 'the topic has been deleted', id: 'CQA57B' },
  failureLabel: { defaultMessage: 'impossible to delete the topic', id: '35lchP' },
});

export function useRemoveTopicWrapper(
  deleteTopicFn: (args: DeleteTopicsArgs) => Promise<any>,
  topicId: string,
) {
  const client = useAppApolloClient();
  const { baseUrl, key: publicKey } = useWebsite();
  return useCallback(
    async (values?: DeleteTopicsArgs2) => {
      try {
        await deleteTopicFn({ pageId: topicId, ...values });
        confirmationToast(<FormattedMessage {...messages.confirmationLabel} />);
        deleteThreadInCache(client, topicId);
        await ssrRevalidateHomeData(baseUrl, publicKey);
      } catch {
        failureToast(<FormattedMessage {...messages.failureLabel} />);
      }
    },
    [baseUrl, publicKey, client, deleteTopicFn, topicId],
  );
}

export function isPendingTopic(topic: Topic | undefined) {
  return topic?.status === 'pending';
}

export function isUnavailableTopic(topic: Topic | undefined) {
  return topic?.status === 'deleted' || topic?.status === 'refused';
}

export function isNotFoundTopic(topic: Topic) {
  return isPendingTopic(topic) || isUnavailableTopic(topic);
}

function maxDateStr(...dates: string[]) {
  return new Date(
    Math.max(...dates.map(d => new Date(d).getTime()).filter(d => !isNaN(d))),
  ).toISOString();
}

export function getTopicLastModifiedStr(topic: Topic) {
  return maxDateStr(topic?.contentEditedAt, topic?.createdAt, topic.lastMessage?.createdAt);
}

export const getAuthorPseudo = (author: Author | undefined, profile?: Profile | undefined) =>
  profile?.pseudo ?? (author as User)?.profile?.pseudo ?? author?.username ?? '';
